import { useEffect, useState, type FormEvent } from 'react';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  useEffect(() => {
    document.title = '关于我们 - 香香漫画韩国漫画免费阅读平台';
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt');
    if (honeypot && (honeypot as string).trim().length > 0) {
      setFormStatus('success');
      setFormMessage('消息已发送，我们会尽快回复您！');
      form.reset();
      return;
    }

    setFormStatus('loading');
    try {
      const payload = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'phone_alt' || !value) {
          payload.append(key, value as string);
        }
      });

      const res = await fetch('https://readdy.ai/api/form/d9peib6b4qjhggaepsjg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await res.text();
      let parsed: any;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (res.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        setFormMessage('消息已发送，我们会尽快回复您！');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || responseText || '发送失败';
        if (serverMsg.includes('spam')) {
          setFormStatus('success');
          setFormMessage('消息已发送，我们会尽快回复您！');
          form.reset();
        } else {
          setFormStatus('error');
          setFormMessage(serverMsg);
        }
      }
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后重试。');
    }
  };

  return (
    <>
      <Header />
      <main className="pt-16">
        <div className="w-full px-4 md:px-6 max-w-[1280px] mx-auto">
          <div className="py-4">
            <Breadcrumb items={[{ label: '关于我们' }]} />
          </div>

          {/* Hero */}
          <section className="py-10 md:py-16 text-center">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-900 mb-4">
              关于香香漫画
            </h1>
            <p className="text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">
              香香漫画致力于为中文读者提供优质的韩国漫画免费阅读服务，打造流畅舒适的下拉式韩漫大全阅读平台。
            </p>
          </section>

          {/* Brand Story */}
          <section className="py-10 md:py-14 max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row gap-8 md:gap-12 items-start">
              <div className="flex-shrink-0 w-full md:w-[280px]">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20cozy%20modern%20reading%20space%20with%20warm%20lighting%2C%20bookshelves%20filled%20with%20colorful%20comics%20and%20manga%2C%20a%20comfortable%20sofa%20with%20cushions%2C%20plants%20in%20the%20corner%2C%20soft%20natural%20light%20from%20window%2C%20creative%20atmosphere%2C%20warm%20cream%20and%20wood%20tones&width=600&height=400&seq=about-brand-story&orientation=landscape"
                  alt="香香漫画品牌故事"
                  className="w-full rounded-xl object-cover"
                />
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-4">品牌故事</h2>
                <div className="text-sm text-foreground-600 leading-relaxed space-y-3">
                  <p>
                    香香漫画成立于2024年，由一群热爱韩国漫画的年轻人创立。我们深知韩漫在国内拥有庞大的读者群体，但由于语言障碍和平台分散，很多读者难以方便地阅读到优质的韩国漫画作品。
                  </p>
                  <p>
                    怀着"让每一位中文读者都能轻松享受韩漫乐趣"的初心，我们打造了这个专注于韩国漫画免费阅读的平台。从最初只有几十部作品的起步阶段，到现在拥有数百部精选韩漫的丰富资源库，香香漫画始终坚持以读者体验为核心。
                  </p>
                  <p>
                    我们采用先进的下拉式阅读技术，让读者在手机和电脑上都能获得流畅舒适的阅读体验。同时，我们与多家韩国漫画平台和作者建立了合作关系，确保内容的合法性和更新速度。
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Values */}
          <section className="py-10 md:py-14 bg-background-100 rounded-2xl px-6 md:px-10 mb-14">
            <h2 className="text-2xl font-heading font-bold text-foreground-900 text-center mb-10">我们的理念</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { icon: 'ri-heart-pulse-line', title: '热爱与专注', desc: '我们是一群真正热爱漫画的人，用心挑选每一部作品，确保为读者带来最优质的阅读内容。' },
                { icon: 'ri-user-heart-line', title: '读者至上', desc: '始终将读者体验放在首位，不断优化平台功能和阅读界面，让每一位用户都能享受愉快的阅读时光。' },
                { icon: 'ri-lightbulb-flash-line', title: '持续创新', desc: '紧跟技术发展潮流，持续改进平台体验，为读者带来更流畅、更便捷的韩漫阅读服务。' },
              ].map((item, idx) => (
                <div key={idx} className="text-center">
                  <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-primary-50 mb-5">
                    <i className={`${item.icon} text-2xl text-primary-500`}></i>
                  </div>
                  <h3 className="text-lg font-heading font-bold text-foreground-900 mb-3">{item.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* User Reviews */}
          <section className="py-10 md:py-14">
            <h2 className="text-2xl font-heading font-bold text-foreground-900 text-center mb-2">读者口碑</h2>
            <p className="text-sm text-foreground-500 text-center mb-10">听听读者们怎么说</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-4xl mx-auto">
              {[
                { name: '小雨', text: '一直在找能看韩漫的平台，香香漫画的下拉式阅读太方便了！内容更新也很快，每天都会来看看有没有新的更新。', rating: 5 },
                { name: '星辰大海', text: '画质清晰，分类齐全，恋爱和奇幻类的韩漫都很好看。推荐给了身边的朋友，大家都说好用！', rating: 5 },
                { name: '漫画控Max', text: '作为资深韩漫读者，香香漫画的体验真的很好。界面简洁，没有广告干扰，阅读体验一级棒！', rating: 4 },
              ].map((review, idx) => (
                <div key={idx} className="bg-background-100 rounded-xl p-6 border border-background-200/70">
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i key={i} className={`text-xs ${i < review.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">&ldquo;{review.text}&rdquo;</p>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 text-sm font-medium">
                      {review.name.charAt(0)}
                    </div>
                    <span className="text-sm font-medium text-foreground-900">{review.name}</span>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Stats */}
          <section className="py-10 md:py-14">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
              {[
                { icon: 'ri-book-read-line', value: '500+', label: '韩漫作品' },
                { icon: 'ri-user-smile-line', value: '200万+', label: '活跃读者' },
                { icon: 'ri-refresh-line', value: '每日更新', label: '更新频率' },
                { icon: 'ri-star-line', value: '4.7', label: '平均评分' },
              ].map((stat, idx) => (
                <div key={idx} className="text-center p-6 rounded-xl bg-background-100 border border-background-200/70">
                  <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-50 mb-3">
                    <i className={`${stat.icon} text-xl text-primary-500`}></i>
                  </div>
                  <div className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-1">{stat.value}</div>
                  <div className="text-xs text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section className="py-10 md:py-14 max-w-2xl mx-auto">
            <h2 className="text-2xl font-heading font-bold text-foreground-900 text-center mb-2">联系我们</h2>
            <p className="text-sm text-foreground-500 text-center mb-8">有任何问题或建议，欢迎随时联系我们</p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
              {[
                { icon: 'ri-mail-line', title: '电子邮箱', value: 'contact@xiangxiangcomic.com' },
                { icon: 'ri-customer-service-line', title: '在线客服', value: '周一至周五 9:00-18:00' },
                { icon: 'ri-chat-smile-2-line', title: '社交媒体', value: '关注微博/公众号' },
              ].map((item, idx) => (
                <div key={idx} className="text-center p-5 rounded-xl bg-background-100 border border-background-200/70">
                  <div className="w-10 h-10 mx-auto flex items-center justify-center rounded-full bg-primary-50 mb-3">
                    <i className={`${item.icon} text-lg text-primary-500`}></i>
                  </div>
                  <div className="text-xs text-foreground-500 mb-1">{item.title}</div>
                  <div className="text-sm font-medium text-foreground-900">{item.value}</div>
                </div>
              ))}
            </div>

            {/* Contact Form */}
            <form onSubmit={handleSubmit} data-readdy-form className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                  <input
                    type="text"
                    id="contact-name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300 transition-all duration-200"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                  <input
                    type="email"
                    id="contact-email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300 transition-all duration-200"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                <input
                  type="text"
                  id="contact-subject"
                  name="subject"
                  required
                  className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300 transition-all duration-200"
                  placeholder="请输入消息主题"
                />
              </div>
              <div>
                <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">消息内容</label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-2.5 rounded-lg bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder-foreground-400 outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300 transition-all duration-200 resize-none"
                  placeholder="请输入您想说的话（500字以内）"
                ></textarea>
              </div>
              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="hp-field"
              />
              <button
                type="submit"
                disabled={formStatus === 'loading'}
                className="w-full px-8 py-3 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-background-50 rounded-full font-medium text-sm transition-all duration-200 whitespace-nowrap"
              >
                {formStatus === 'loading' ? '发送中...' : '发送消息'}
              </button>
              {formStatus !== 'idle' && (
                <p className={`text-center text-sm ${formStatus === 'success' ? 'text-accent-600' : 'text-red-500'}`}>
                  {formMessage}
                </p>
              )}
            </form>
          </section>

          {/* FAQ */}
          <section className="py-10 md:py-14 max-w-3xl mx-auto">
            <h2 className="text-2xl font-heading font-bold text-foreground-900 text-center mb-8">常见问题</h2>
            <div className="space-y-3">
              {[
                { q: '香香漫画的韩漫都是免费的吗？', a: '是的，香香漫画平台上所有韩国漫画均为免费阅读，无需付费即可畅享全部内容。' },
                { q: '多久更新一次漫画内容？', a: '热门连载漫画每日更新，确保读者能够第一时间看到最新章节。新作品也在持续上架中。' },
                { q: '支持哪些设备阅读？', a: '香香漫画支持在手机、平板和电脑上阅读，采用响应式设计，在任何设备上都能获得流畅的下拉式阅读体验。' },
                { q: '如何搜索我喜欢的漫画？', a: '您可以使用页面顶部的搜索框输入漫画名称或作者进行搜索，也可以通过分类页面按类型浏览。' },
                { q: '漫画内容是否正版？', a: '我们与多家韩国漫画平台和作者建立了合作关系，努力确保平台内容的合法性。' },
              ].map((faq, idx) => (
                <details key={idx} className="group bg-background-100 rounded-xl border border-background-200/70 overflow-hidden">
                  <summary className="flex items-center justify-between px-6 py-4 cursor-pointer list-none">
                    <span className="text-sm font-medium text-foreground-900">{faq.q}</span>
                    <i className="ri-add-line text-foreground-400 group-open:hidden text-sm"></i>
                    <i className="ri-subtract-line text-foreground-400 hidden group-open:block text-sm"></i>
                  </summary>
                  <div className="px-6 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </section>
        </div>

        {/* FAQ Schema */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          'mainEntity': [
            { '@type': 'Question', 'name': '香香漫画的韩漫都是免费的吗？', 'acceptedAnswer': { '@type': 'Answer', 'text': '是的，香香漫画平台上所有韩国漫画均为免费阅读，无需付费即可畅享全部内容。' } },
            { '@type': 'Question', 'name': '多久更新一次漫画内容？', 'acceptedAnswer': { '@type': 'Answer', 'text': '热门连载漫画每日更新，确保读者能够第一时间看到最新章节。新作品也在持续上架中。' } },
            { '@type': 'Question', 'name': '支持哪些设备阅读？', 'acceptedAnswer': { '@type': 'Answer', 'text': '香香漫画支持在手机、平板和电脑上阅读，采用响应式设计，在任何设备上都能获得流畅的下拉式阅读体验。' } },
            { '@type': 'Question', 'name': '如何搜索我喜欢的漫画？', 'acceptedAnswer': { '@type': 'Answer', 'text': '您可以使用页面顶部的搜索框输入漫画名称或作者进行搜索，也可以通过分类页面按类型浏览。' } },
            { '@type': 'Question', 'name': '漫画内容是否正版？', 'acceptedAnswer': { '@type': 'Answer', 'text': '我们与多家韩国漫画平台和作者建立了合作关系，努力确保平台内容的合法性。' } },
          ],
        }) }} />

        {/* About Page Schema */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          'name': '关于香香漫画',
          'description': '香香漫画是在线看韩漫的官网，提供韩国漫画免费阅读和下拉式韩漫大全，内容优质，浏览流畅。',
          'about': {
            '@type': 'Organization',
            'name': '香香漫画',
            'description': '韩国漫画免费阅读平台',
          },
        }) }} />
      </main>
      <Footer />
    </>
  );
}